import { AsyncPipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, inject } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { NgbHighlight, NgbPopover, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  finalize,
  first,
  map,
  merge,
  mergeMap,
  Observable,
  switchMap,
  tap,
} from 'rxjs';
import { CoreModule } from 'src/app/core/core.module';
import { ResourceTypes } from 'src/app/core/enums/resource-types.enum';
import { UserCompanyPermissions } from 'src/app/core/enums/user-company-permissions.enum';
import { Contact } from 'src/app/core/models/contact/contact.interface';
import { NewOrganizationContactFormModel } from 'src/app/core/models/organization/new-organization-contact-form.model';
import { MinimalProfileWithPhoneNumber } from 'src/app/core/models/user/profile';
import { ContactService } from 'src/app/core/services/contact.service';
import { FileService } from 'src/app/core/services/file.service';
import { PermissionService } from 'src/app/core/services/permission.service';
import { ObservableUtil } from 'src/app/core/utils/observable-util';
@Component({
  selector: 'app-organizaiton-contacts',
  imports: [
    NgbPopover,
    NgbTooltip,
    TranslateModule,
    CoreModule,
    ReactiveFormsModule,
    NgbHighlight,
    RouterLink,
    AsyncPipe,
  ],
  templateUrl: './organization-contacts.html',
  styleUrl: './organization-contacts.scss',
  standalone: true,
  providers: [NewOrganizationContactFormModel],
})
export class OrganizationContacts implements OnChanges {
  newOrganizationContactForm = inject(NewOrganizationContactFormModel);
  private readonly contactService = inject(ContactService);
  private readonly permissionService = inject(PermissionService);
  private readonly fileService = inject(FileService);

  @Input({ required: true })
  public companyId = '';

  @Input({ required: true })
  public organizationId = '';

  @Output() public createContactRequested = new EventEmitter<void>();

  public isLoading = false;

  public readonly canCreateContacts$ = this.permissionService.userPermissions$.pipe(
    map(permissions =>
      this.permissionService.hasPermission(
        permissions,
        ResourceTypes.Contact,
        UserCompanyPermissions.Create,
      ),
    ),
  );

  public contactFullNameFilter: (contact: Contact, term: string) => boolean = (contact, term) =>
    contact.fullName?.toLowerCase().includes(term.toLowerCase());

  public contactFormatter: (contact: Contact | null | undefined) => string = contact =>
    contact?.fullName ?? '';

  public contactCompareFn: (contact: Contact, value: Contact) => boolean = (contact, value) =>
    contact.fullName === value.fullName;

  public showAll$ = new BehaviorSubject<boolean>(false);

  private readonly companyId$ = new BehaviorSubject<string | null>(null);
  private readonly organizationId$ = new BehaviorSubject<string | null>(null);

  public companyContacts$ = this.companyId$.pipe(
    filter((companyId): companyId is string => Boolean(companyId)),
    switchMap(companyId => this.contactService.getCompanyContacts(companyId)),
  );

  public organizationContacts$ = combineLatest([this.companyId$, this.organizationId$]).pipe(
    filter((ids): ids is [string, string] => Boolean(ids[0]) && Boolean(ids[1])),
    switchMap(([companyId, organizationId]) =>
      this.contactService.getOrganizationContacts(companyId, organizationId),
    ),
  );

  public organizationContactProfiles$ = this.organizationContacts$.pipe(
    map(contacts =>
      contacts.map(contact => {
        const profile: MinimalProfileWithPhoneNumber = {
          id: contact.id,
          fullName: contact.fullName,
          imageUrl: '',
          avatarImageExtension: contact.avatarImageExtension,
          phoneNumber: contact.phoneNumber,
        };

        return profile;
      }),
    ),
    mergeMap(profilesWithNumber =>
      ObservableUtil.loadEmployeesWithImage$(profilesWithNumber, this.fileService),
    ),
  );

  public readonly displayedProfiles$ = combineLatest([
    this.organizationContactProfiles$,
    this.showAll$,
  ]).pipe(map(([contacts, showAll]) => (showAll ? contacts : contacts.slice(0, 3))));

  public readonly displayedContacts$ = combineLatest([
    this.organizationContacts$,
    this.showAll$,
  ]).pipe(map(([contacts, showAll]) => (showAll ? contacts : contacts.slice(0, 3))));

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['organizationId'] || !this.companyId || !this.organizationId) {
      return;
    }

    this.contactService.setContactsUrl(this.companyId);

    this.companyId$.next(this.companyId);
    this.organizationId$.next(this.organizationId);

    const loads: Observable<unknown>[] = [];

    if (!this.contactService.hasOrganizationContacts(this.companyId, this.organizationId)) {
      loads.push(this.contactService.loadOrganizationContacts(this.companyId, this.organizationId));
    }

    if (!this.contactService.hasCompanyContacts(this.companyId)) {
      loads.push(this.contactService.loadCompanyContacts(this.companyId, this.organizationId));
    }

    if (!loads.length) {
      return;
    }

    this.isLoading = true;

    merge(...loads)
      .pipe(
        finalize(() => {
          this.isLoading = false;
        }),
      )
      .subscribe();
  }

  public addContact(popover: NgbPopover): void {
    const contactId = this.newOrganizationContactForm.form.get('newContactId')?.value;

    if (!contactId) {
      return;
    }

    this.isLoading = true;

    this.contactService
      .patchContact(contactId, 'organizationId', this.organizationId)
      .pipe(
        first(),
        tap(() => {
          const contact = this.contactService.getCompanyContactById(this.companyId, contactId);

          if (!contact) {
            return;
          }

          this.contactService.addToOrganization(this.companyId, this.organizationId, contact);
        }),
        finalize(() => (this.isLoading = false)),
      )
      .subscribe();

    popover.close();
  }

  public openCreateContactModal(popover?: NgbPopover): void {
    popover?.close();
    this.createContactRequested.emit();
  }

  public toggleContacts(): void {
    this.showAll$.next(!this.showAll$.value);
  }
}
